import { useState } from "react";
import { Check, X, Info } from "lucide-react";
import { currencyFormat } from "../../utils";
import { useBookingContext } from "../../context/BookingContext";
import { useTranslation } from "react-i18next";

const InsuranceSelector = ({ formik, t, insurances, insuranceLoading }) => {
  const { data, setData } = useBookingContext();
  const { i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempSelectedInsurance, setTempSelectedInsurance] = useState(null);

  const insuranceOptions = [
    {
      id: "no_extra_protection",
      name: "No Extra Protection",
      price: 0,
      description: [
        {
          icon: "check_icon",
          lang: "id",
          text: "10% diskon dari Harga Publish",
        },
        {
          icon: "check_icon",
          lang: "id",
          text: "Kilometer tidak terbatas",
        },
        {
          icon: "x_icon",
          lang: "id",
          text: "Semua biaya perbaikan menjadi tanggung jawab penyewa",
        },
        {
          icon: "x_icon",
          lang: "id",
          text: "Kerusakan akibat tabrakan, goresan, dan benturan",
        },
        {
          icon: "x_icon",
          lang: "id",
          text: "Perlindungan Ban dan Kaca",
        },
        {
          icon: "x_icon",
          lang: "id",
          text: "Perlindungan Interior",
        },

        {
          icon: "check_icon",
          lang: "en",
          text: "10% discount from Published Rate",
        },
        {
          icon: "check_icon",
          lang: "en",
          text: "Unlimited mileage",
        },
        {
          icon: "x_icon",
          lang: "en",
          text: "All repair costs are renter's responsibility",
        },
        {
          icon: "x_icon",
          lang: "en",
          text: "No coverage for collision, scratches, or impact damage",
        },
        {
          icon: "x_icon",
          lang: "en",
          text: "No tire and glass protection",
        },
        {
          icon: "x_icon",
          lang: "en",
          text: "No interior protection",
        },
      ],
      subtitle: "10% off the Published Price",
      insurance_code: "no_extra_protection",
    },
    {
      id: "basic_protection",
      name: "Basic Protection",
      price: 0,
      description: [
        {
          icon: "check_icon",
          lang: "id",
          text: "Kilometer tidak terbatas",
        },
        {
          icon: "check_icon",
          lang: "id",
          text: "Dengan biaya own risk Rp 750.000/titik kecelakaan",
        },
        {
          icon: "check_icon",
          lang: "id",
          text: "Kerusakan akibat tabrakan, goresan, dan benturan",
        },
        {
          icon: "check_icon",
          lang: "id",
          text: "Perlindungan Ban dan Kaca",
        },
        {
          icon: "x_icon",
          lang: "id",
          text: "Perlindungan Interior",
        },
        {
          icon: "x_icon",
          lang: "id",
          text: "Biaya tidak termasuk jika diperlukan derek mobil dan biaya sewa lebih dari 7 hari selama perbaikan di bengkel",
        },

        {
          icon: "check_icon",
          lang: "en",
          text: "Unlimited mileage",
        },
        {
          icon: "check_icon",
          lang: "en",
          text: "Collision deductible fee IDR 750,000 per incident",
        },
        {
          icon: "check_icon",
          lang: "en",
          text: "Coverage for collision, scratches, and impact damage",
        },
        {
          icon: "check_icon",
          lang: "en",
          text: "Tire and glass protection included",
        },
        {
          icon: "x_icon",
          lang: "en",
          text: "No interior protection",
        },
        {
          icon: "x_icon",
          lang: "en",
          text: "Tow truck cost & rental extension above 7 days during repair not included",
        },
      ],
      subtitle: "Publish Price",
      insurance_code: "basic_protection",
    },
  ];

  const newInsurance = insuranceOptions.concat(insurances).sort((a, b) => a.price - b.price);

  const getDescriptionByLang = (option, lang = "id") => {
    return option.description.filter((item) => item.lang === lang);
  };

  const getCheckItems = (items) => items.filter((item) => item.icon === "check_icon");

  const getXItems = (items) => items.filter((item) => item.icon === "x_icon");

  const selectedInsurance =
    newInsurance.find((opt) => opt.id === (formik.values.insurance_id || "basic_protection")) || newInsurance[1];

  const openModal = () => {
    setTempSelectedInsurance(formik.values.insurance_id);
    setIsModalOpen(true);
  };

  const handleSelectInsuranceTemp = (insuranceId) => {
    setTempSelectedInsurance(insuranceId);
  };

  const calculateDiscountedPrice = (price, discount) => {
    if (!discount) return price;
    return price - (price * discount) / 100;
  };

  const getDiscount = (option) => {
    if (option.id === "no_extra_protection") {
      return 10;
    }
    return 0;
  };

  return (
    <>
      <div className="mt-6 booking-modal__person-info">
        <h4 className="flex items-center gap-2">{t?.("insurance_protection") || "Perlindungan Asuransi"}</h4>
        <div
          onClick={openModal}
          className="p-4 mt-4 transition-all border-2 border-gray-300 rounded-lg cursor-pointer hover:border-[#f5272b] hover:shadow-md"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h5 className="text-xl font-bold text-gray-800">{selectedInsurance.name}</h5>
                <div className="flex gap-1">
                  {[...Array(3)].map((_, i) => (
                    <span key={i} className="text-yellow-500 text-xl">
                      ★
                    </span>
                  ))}
                </div>
              </div>

              {getDiscount(selectedInsurance) > 0 && (
                <span className="inline-block px-2 py-1 mb-2 text-xs font-semibold text-red-600 bg-red-100 rounded">
                  - {t?.("online_discount") || "Diskon Online"} {getDiscount(selectedInsurance)}%
                </span>
              )}

              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-red-600">
                  {Number(selectedInsurance.price) === 0
                    ? t?.("free") || "Gratis"
                    : currencyFormat(selectedInsurance.price)}
                </span>

                <span className="text-md text-gray-600">/{t?.("day") || "hari"}</span>
              </div>
              <p className="mt-1 text-md text-gray-600">{selectedInsurance.subtitle}</p>
            </div>

            <button
              type="button"
              className="px-4 py-2 text-xl font-semibold text-white transition-colors bg-[#f5272b] rounded hover:bg-red-600"
            >
              {t?.("change") || "Ubah"}
            </button>
          </div>

          <div className="pt-3 mt-3 border-t">
            <p className="flex items-center gap-2 text-sm text-gray-600">
              <Info className="w-4 h-4" />
              {t?.("click_to_see_details") || "Klik untuk melihat detail perlindungan"}
            </p>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="relative w-full max-w-5xl overflow-hidden bg-white rounded-lg shadow-2xl max-h-[90vh] flex flex-col">
            <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-white border-b">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">
                  {t?.("choose_insurance") || "Pilih Paket Perlindungan"}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t?.("select_best_protection") || "Pilih paket yang sesuai dengan kebutuhan Anda"}
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-gray-500 transition-colors rounded-full hover:bg-gray-100"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {newInsurance.map((option) => {
                  const isSelected = tempSelectedInsurance === option.id;
                  const discount = getDiscount(option);
                  const finalPrice = calculateDiscountedPrice(option.price, discount);
                  const idDescriptions = getDescriptionByLang(option, i18n.language);
                  const checkItems = getCheckItems(idDescriptions);
                  const xItems = getXItems(idDescriptions);

                  return (
                    <div
                      key={option.id}
                      onClick={() => handleSelectInsuranceTemp(option.id)}
                      className={`relative p-6 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? "border-[#f5272b] bg-blue-50 shadow-lg transform scale-105"
                          : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-0 right-0 px-3 py-1 text-xs font-bold text-white bg-[#f5272b] rounded-tr-lg rounded-bl-lg">
                          ✓ {t?.("selected") || "Dipilih"}
                        </div>
                      )}

                      <div className="mb-4 text-center">
                        <h4 className="text-xl font-bold text-gray-800">{option.name}</h4>
                        <div className="flex justify-center gap-1 my-2">
                          {[...Array(3)].map((_, i) => (
                            <span key={i} className={`text-2xl text-yellow-500`}>
                              ★
                            </span>
                          ))}
                        </div>

                        {discount > 0 && (
                          <div className="inline-block px-2 py-1 mb-2 text-xs font-semibold text-red-600 bg-red-100 rounded">
                            - {t?.("online_discount") || "Diskon Online"} {discount}%
                          </div>
                        )}

                        <p className="text-sm text-gray-600">{option.subtitle}</p>
                      </div>

                      <div className="py-4 mb-4 text-center border-t border-b">
                        <div className="text-3xl font-bold text-red-600">
                          {finalPrice === 0 ? t?.("free") || "Gratis" : currencyFormat(finalPrice)}
                        </div>
                        {discount > 0 && (
                          <div className="mt-1 text-sm text-gray-400 line-through">{currencyFormat(option.price)}</div>
                        )}
                        <div className="text-xs text-gray-600">
                          {t?.("additional_cost_per_day") || "Biaya tambahan / hari"}
                        </div>
                      </div>

                      <div className="mb-4 space-y-2">
                        {checkItems.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Check className="flex-shrink-0 w-5 h-5 mt-0.5 text-green-600" />
                            <span className="text-sm text-gray-700">{item.text}</span>
                          </div>
                        ))}
                        {xItems.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2 opacity-50">
                            <X className="flex-shrink-0 w-5 h-5 mt-0.5 text-gray-400" />
                            <span className="text-sm text-gray-500">{item.text}</span>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        className={`w-full py-3 font-bold rounded transition-colors ${
                          isSelected ? "bg-[#f5272b] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {isSelected ? `✓ ${t?.("selected") || "Terpilih"}` : t?.("select_package") || "Pilih Paket"}
                      </button>

                      {option.insurance_code === "basic" && (
                        <div className="mt-3 text-xs text-center text-gray-500">
                          {t?.("most_popular_choice") || "Pilihan yang paling populer"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="p-4 mt-12 border-l-4 border-[#f5272b] rounded bg-blue-50">
                <div className="flex items-start gap-3">
                  <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-[#f5272b]" />
                  <div>
                    <h5 className="mb-2 font-semibold text-gray-800">
                      {t?.("insurance_info_title") || "Informasi Penting"}
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li>
                        {t?.("insurance_info_description") ||
                          "Kami selalu bertujuan untuk memberikan perlindungan terbaik kepada pelanggan kami. Kami menyediakan beberapa pilihan asuransi yang dapat Anda pilih sesuai kebutuhan Anda. Asuransi ini akan menjadi acuan dan perlindungan jika terjadi hal buruk pada Anda"}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 flex gap-4 p-6 bg-white border-t">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-6 py-3 font-bold text-gray-700 transition-colors bg-gray-200 rounded hover:bg-gray-300"
              >
                {t?.("back") || "Kembali"}
              </button>
              <button
                type="button"
                onClick={() => {
                  const selectedInsurance = newInsurance.find((i) => i.id === tempSelectedInsurance);
                  formik.setFieldValue("insurance_id", tempSelectedInsurance);
                  formik.setFieldValue("insurance_code", selectedInsurance.insurance_code);
                  setData({
                    ...data,
                    insurance_id: tempSelectedInsurance,
                    insurance_code: selectedInsurance.insurance_code,
                  });

                  setIsModalOpen(false);
                }}
                className="flex-1 px-6 py-3 font-bold text-white transition-colors bg-[#f5272b] rounded hover:bg-red-600"
              >
                {t?.("confirm_selection") || "Konfirmasi Pilihan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InsuranceSelector;
